import * as React from 'react'
import styled from 'styled-components'
import { SwiperOptions, Swiper as SwiperInstance } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Button } from '../../components/UI/Button'
import { TitleWithPrefix } from '../../components/UI/TitleWithPrefix'
import { containerDefault } from '../../lib/styles/Container'
import { useIsOverflow } from '../../lib/utils/isHorizontalOverflow'
import { TeaserCardItem } from '../TeaserCardItem'
import { TeaserCardProductItem } from '../TeaserCardProductItem'
import { TeaserCardGridProps } from './types'

const OuterWrapper = styled.div<{ showOnDesktop?: boolean }>`
  overflow: hidden;
  display: ${p => (p.showOnDesktop ? 'block' : 'none')};

  @media screen and (max-width: 860px) {
    display: block;
  }
`

const Wrapper = styled.div`
  ${containerDefault}
  margin-inline: auto;

  .swiper {
    overflow: visible;
    margin-inline: -0.5rem;
  }

  .swiper-slide {
    height: auto;
    width: auto;

    & > * {
      height: 100%;
    }
  }
`

const SlideInner = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.5rem;
  width: calc(61rem / 3);
  max-width: 24em;
  min-width: 20rem;

  @media screen and (max-width: 1500px) {
    width: calc((80vw + 1rem) / 3);
  }

  @media screen and (max-width: 1100px) {
    width: 17rem;
  }

  @media screen and (max-width: 860px) {
    width: 85vw;
  }

  & > * {
    height: 100%;
  }
`

const SliderHeader = styled.header<{
  align?: 'left' | 'center'
  $mediumCenterMargin?: string
  $smallCenterMargin?: string
  $mediumLeftMargin?: string
  $smallLeftMargin?: string
}>`
  display: flex;
  gap: 2rem;
  justify-content: ${({ align }) => (align === 'center' ? 'center' : 'space-between')};
  margin-bottom: ${p => p.$mediumCenterMargin || p.$smallCenterMargin || p.$mediumLeftMargin || p.$smallLeftMargin};
`

const SliderTitle = styled(TitleWithPrefix)``

const StyledNavButtons = styled.div<{ align?: 'left' | 'center' }>`
  align-items: center;
  justify-content: center;
  display: flex;
  flex: 0 0 auto;
  margin-left: auto;
  margin-top: ${({ align }) => (align === 'center' ? '0.5rem' : '0')};

  @media screen and (max-width: 860px) {
    display: none;
  }
`

const sliderOptions: SwiperOptions = {
  slidesPerView: 'auto',
}

interface SliderProps {
  showOnDesktop?: boolean
  mediumCenterMargin: string
  smallCenterMargin: string
  mediumLeftMargin: string
  smallLeftMargin: string
}

const NavButtons = ({ align, swiper }) => {
  return (
    <StyledNavButtons align={align}>
      <Button
        $size="small"
        $variant="tertiary"
        iconLeft="arrow-left"
        $ignoreTheme
        onClick={() => swiper && swiper.slidePrev()}
      />
      <Button
        $size="small"
        $variant="tertiary"
        iconLeft="arrow-right"
        $ignoreTheme
        onClick={() => swiper && swiper.slideNext()}
      />
    </StyledNavButtons>
  )
}

export const Slider = ({
  blok,
  showOnDesktop,
  mediumCenterMargin,
  mediumLeftMargin,
  smallCenterMargin,
  smallLeftMargin,
}: TeaserCardGridProps & SliderProps) => {
  const [swiper, setSwiper] = React.useState<SwiperInstance | null>(null)

  const refSlider = React.useRef()
  const isOverflow = useIsOverflow(refSlider, isOverflowFromCallback => {
    return isOverflowFromCallback
  })
  const isCentered = blok.alignment === 'center'

  return (
    <OuterWrapper showOnDesktop={showOnDesktop}>
      <Wrapper>
        <SliderHeader
          align={blok.alignment}
          $mediumCenterMargin={mediumCenterMargin}
          $mediumLeftMargin={mediumLeftMargin}
          $smallCenterMargin={smallCenterMargin}
          $smallLeftMargin={smallLeftMargin}
        >
          {blok.title && (
            <SliderTitle
              title={blok.title}
              headlineSize={blok.headlineSize}
              textAlign={blok.alignment}
              headlineType={blok.headlineSyntax}
            />
          )}
          {showOnDesktop && isOverflow && !isCentered && <NavButtons align={blok.alignment} swiper={swiper} />}
        </SliderHeader>
        <Swiper onSwiper={swiper => setSwiper(swiper)} {...sliderOptions} ref={refSlider}>
          {blok.items.map(item => (
            <SwiperSlide key={item._uid}>
              <SlideInner>
                {item.component === 'TeaserCardItem' ? (
                  <TeaserCardItem blok={item} />
                ) : (
                  <TeaserCardProductItem blok={item} />
                )}
              </SlideInner>
            </SwiperSlide>
          ))}
        </Swiper>
        {showOnDesktop && isOverflow && isCentered && <NavButtons align={blok.alignment} swiper={swiper} />}
      </Wrapper>
    </OuterWrapper>
  )
}
